import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { NARROW_QUERY } from './layoutStyles';
import { WS_COLOR, WS_LAYOUT } from './tokens';

export interface FieldGroupProps {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}

/**
 * A titled group of fields for settings and administration: the explanation on the left, the
 * controls on the right; stacked when the layout is narrow (a container query on the nearest
 * size container, which every layout is).
 */
export function FieldGroup({ title, description, children }: FieldGroupProps) {
  return (
    <Box
      role="group"
      aria-label={title}
      sx={{
        display: 'grid',
        gridTemplateColumns: `${WS_LAYOUT.fieldGroupLabel}px minmax(0, 1fr)`,
        gap: '8px 32px',
        py: 5,
        borderTop: `1px solid ${WS_COLOR.border}`,
        '&:first-of-type': { borderTop: 0, pt: 0 },
        [NARROW_QUERY]: { gridTemplateColumns: 'minmax(0, 1fr)' },
      }}
    >
      <Box>
        <Typography variant="h3" component="h3" sx={{ fontSize: 15, lineHeight: '22px' }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" sx={{ color: WS_COLOR.textMuted, mt: 0.5 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'grid', gap: 4, justifyItems: 'start', minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
